import { GenesisEngine } from "./genesis/GenesisEngine";
import { Hud, type Difficulty } from "./genesis/Hud";
import { AudioSystem } from "./genesis/AudioSystem";
import { WorldManager } from "./genesis/WorldManager";
import { CapacityManager, LocalCapacitySim } from "./genesis/CapacityQueue";
import { MultiplayerSystem } from "./genesis/MultiplayerSystem";
import { CiteHub } from "./hub/CiteHub";
import { GAMES, firstScene } from "./content/registry";
import { t } from "./genesis/i18n";

/**
 * Kingdom Quest 3D — boot.
 * La Cité (hub of 15 games) → capacity/queue gate → play a scene → back to hub.
 */
const canvas = document.getElementById("renderCanvas") as HTMLCanvasElement;
const gen = new GenesisEngine(canvas);
gen.start();

const audio = new AudioSystem();

// Server-capacity gate. `?capacity=rest[&api=<base>]` wires the REAL
// server-authoritative family-fun capacity API (PR 298, fail-open);
// default = local simulator so the standalone preview demos the queue.
import { RestCapacityTransport } from "./genesis/CapacityQueue";
const params = new URLSearchParams(location.search);
let transport;
if (params.get("capacity") === "rest") {
  transport = new RestCapacityTransport(params.get("api") ?? "");
} else {
  const sim = new LocalCapacitySim(3);
  sim.seed(GAMES.map((g) => g.slug));
  transport = sim;
}
const capacity = new CapacityManager(transport);

// v9 MULTIPLAYER — `?group=<CODE>&name=<pseudo>` joins the product's REAL
// family-fun WebSocket relay (family-fun-service NETWORK-MP). Host defaults to
// dev.kconnection.org (`?mphost=` overrides, ws:// for localhost). `?mprelay=local`
// swaps in the clearly-marked BroadcastChannel FALLBACK (same-machine tabs) for
// offline engine proof. Fail-open: relay unreachable → silent solo.
const groupCode = (params.get("group") || "").trim().toUpperCase();
let mp: MultiplayerSystem | null = null;
if (groupCode) {
  let storedName = "";
  try {
    storedName = localStorage.getItem("kq_name") || "";
  } catch { /* ignore */ }
  mp = new MultiplayerSystem({
    code: groupCode,
    name: params.get("name") || storedName || "Pèlerin",
    host: params.get("mphost") || undefined,
    token: params.get("token") || undefined,
    relay: params.get("mprelay") === "local" ? "local" : "ws",
  });
  mp.connect();
  // Test/debug introspection hook (used by the 2-window multiplayer proof).
  (window as unknown as { __kqmp: MultiplayerSystem }).__kqmp = mp;
}

const hub = new CiteHub(capacity);
let difficulty: Difficulty = "D";

function showHub(): void {
  hub.show(launch);
}

async function launch(slug: string, skipQueue = false, sceneIdx = 0): Promise<void> {
  const game = GAMES.find((g) => g.slug === slug);
  const scene = game?.scenes[sceneIdx] ?? firstScene(slug);
  if (!scene || !game) return;
  hub.hide();
  audio.resume(); // user gesture → enable sound

  const hud = new Hud();
  const world = new WorldManager(gen, hud, audio, mp);

  try {
    const seat = skipQueue
      ? { release: () => {} }
      : await capacity.enter(slug, t(scene.title), hud.adt);
    world.run(scene, difficulty, () => {
      world.dispose();
      hud.dispose();
      // Multi-scene parcours: chain to the next scene of the SAME game
      // (seat kept — same game session); the hub returns after the last one.
      if (game.scenes[sceneIdx + 1]) {
        void launch(slug, true, sceneIdx + 1);
      } else {
        seat.release();
        showHub();
      }
    });
    console.log("KQ scene running:", slug);
    // keep chosen difficulty across sessions
    hud.onDifficulty = ((prev) => (d: Difficulty) => {
      difficulty = d;
      prev?.(d);
    })(hud.onDifficulty);
  } catch (e) {
    // queue cancelled or scene build error → back to the city
    console.error("KQ launch error:", (e as Error)?.stack || e);
    hud.dispose();
    showHub();
  }
}

// Dev/deep-link hook: ?scene=<slug> boots straight into a game (skips the hub).
(window as unknown as { kqLaunch: (s: string) => void }).kqLaunch = launch;
const _direct = new URLSearchParams(location.search).get("scene");
if (_direct && GAMES.some((g) => g.slug === _direct)) {
  const _idx = Math.max(0, parseInt(params.get("idx") ?? "0", 10) || 0);
  void launch(_direct, true, _idx); // direct/deep-test boot bypasses the capacity queue
} else {
  showHub();
}
